import {
  LocationPlan,
  ProgramType,
  RateBase,
  RateGroup,
} from "@Light/services/lightTypes";
import type {
  EVChargingPricingTierSchema,
  EVChargingScheduleSchema,
  PlanResponse,
} from "@Light/services/lightTypes";

export function formatHourString(hour: number, ampm: string): string {
  return `${hour} ${ampm}`;
}

export function hourString(hour: number): string {
  if (hour > 12) {
    return formatHourString(hour - 12, "PM");
  }

  if (hour === 12) {
    return formatHourString(hour, "PM");
  }

  if (hour === 0) {
    return formatHourString(12, "AM");
  }

  return formatHourString(hour, "AM");
}

export function formatTimeAsAmPm(time: string): string {
  const hour = parseInt(time.split(":")[0], 10);
  if (Number.isNaN(hour)) return time;
  return hourString(hour);
}

export type PlanFeatures = {
  fixedRate?: {
    termMonths: number;
  };
  renewableEnergy?: {};
  noCancellationFees?: {};
  preactivationCredit?: {
    percent: number;
  };
  solarBuyback?: {
    baseChargeText: string;
  };
  storageCredit?: {};

  vehicleCharging?: {
    baseFee: number;
    schedules: EVChargingScheduleSchema[];
    onScheduleRates: EVChargingPricingTierSchema[];
    chargeStart?: string;
    chargeEnd?: string;
  };
};

export function planFeaturesForRateGroup(
  rate: RateGroup | RateBase | PlanResponse | LocationPlan,
): PlanFeatures {
  const planFeatures: PlanFeatures = {};

  if (rate.term_months) {
    planFeatures.fixedRate = {
      termMonths: rate.term_months,
    };
  }

  if ((rate.renewable_percent ?? 0) >= 100) {
    planFeatures.renewableEnergy = {};
  }

  if (rate.early_termination_fee_dollars === "0.00") {
    planFeatures.noCancellationFees = {};
  }

  if (rate.solar_preactivation_credit_percent > 0) {
    planFeatures.preactivationCredit = {
      percent: rate.solar_preactivation_credit_percent,
    };
  }

  if (rate.has_solar_buyback) {
    planFeatures.solarBuyback = {
      baseChargeText: `$${rate.plan_monthly_cost_dollars}`,
    };
  }

  if (rate.device_program_terms) {
    const ev_program = rate.device_program_terms.find(
      (x) => x.program_type === ProgramType.Evcharging,
    );

    if (ev_program?.ev_charging) {
      const schedules = ev_program.ev_charging.schedules ?? [];
      const firstSchedule = schedules[0];
      planFeatures.vehicleCharging = {
        baseFee: parseFloat(ev_program.ev_charging.base_fee ?? "0"),
        schedules,
        onScheduleRates: ev_program.ev_charging.on_schedule_rates ?? [],
        chargeStart: firstSchedule
          ? formatTimeAsAmPm(firstSchedule.start_time)
          : undefined,
        chargeEnd: firstSchedule
          ? formatTimeAsAmPm(firstSchedule.end_time)
          : undefined,
      };
    }
    const battery_program = rate.device_program_terms.find(
      (x) => x.program_type === ProgramType.Battery,
    );

    if (battery_program) {
      planFeatures.storageCredit = {};
    }
  }

  return planFeatures;
}
